import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { MoreHorizontal, Check, X, Eye, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { PageHeader, DataTable, Column, MetricCard, KPIGrid } from '@/components/common';
import { approvalsApi, Approval, QueryApprovalParams } from '@/api/services';
import { rfqApi } from '@/api/services/tms.api';
import { consumptionApi } from '@/api/services/consumption.api';
import { Link } from 'react-router-dom';
import { getErrorMessage } from '@/api/client';
import { formatCurrency as formatMoney } from '@/lib/utils';

const statusColors: Record<string, string> = {
  PENDING: 'bg-warning-50 text-warning-600',
  APPROVED: 'bg-success-50 text-success-600',
  REJECTED: 'bg-danger-50 text-danger-600',
};

export default function Approvals() {
  const navigate = useNavigate();
  const [approvals, setApprovals] = useState<Approval[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('PENDING');
  const [decisionDialog, setDecisionDialog] = useState<{
    open: boolean;
    approval?: Approval;
    decision?: 'APPROVED' | 'REJECTED';
  }>({ open: false });
  const [comments, setComments] = useState('');
  // Two other queues exist, on different models with their own decide
  // endpoints: freight awards and consumption variances. They are counted here
  // rather than merged, so that "what is waiting on me" has one place to look
  // while each decision stays where the module that understands it can make it.
  const [elsewhere, setElsewhere] = useState<{ freight: number; consumption: number } | null>(null);
  const [processing, setProcessing] = useState(false);

  const fetchApprovals = async (params?: QueryApprovalParams) => {
    try {
      setLoading(true);
      const response = await approvalsApi.getAll(params);
      setApprovals(response.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.allSettled([rfqApi.getMyPendingApprovals(), consumptionApi.getPendingApprovals()]).then(
      ([freight, consumption]) =>
        setElsewhere({
          freight: freight.status === 'fulfilled' ? (freight.value?.length ?? 0) : 0,
          consumption:
            consumption.status === 'fulfilled' ? (consumption.value.data?.length ?? 0) : 0,
        })
    );
  }, []);

  useEffect(() => {
    const params: QueryApprovalParams = {};
    if (statusFilter !== 'all') {
      params.status = statusFilter as Approval['status'];
    }
    fetchApprovals(params);
  }, [statusFilter]);

  const handleDecision = async () => {
    if (!decisionDialog.approval || !decisionDialog.decision) return;

    try {
      setProcessing(true);
      await approvalsApi.decide(
        decisionDialog.approval.id,
        decisionDialog.decision,
        comments || undefined
      );
      toast.success(`Request ${decisionDialog.decision.toLowerCase()}`);
      setDecisionDialog({ open: false });
      setComments('');
      fetchApprovals({
        status: statusFilter !== 'all' ? (statusFilter as Approval['status']) : undefined,
      });
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  /**
   * Delegates to the shared formatter so the currency comes from the record
   * rather than from a hardcoded 'USD' in this file. A page-local formatter is
   * how an order billed in dirhams ends up rendered with a dollar sign.
   */
  const formatCurrency = (amount?: number | null, currency = 'USD') =>
    amount == null ? '-' : formatMoney(amount, currency);

  // Calculate metrics
  const pendingCount = approvals.filter((a) => a.status === 'PENDING').length;
  const approvedCount = approvals.filter((a) => a.status === 'APPROVED').length;
  const rejectedCount = approvals.filter((a) => a.status === 'REJECTED').length;

  const columns: Column<Approval>[] = [
    {
      key: 'entityType',
      linkTo: (a) => (a.purchaseOrderId ? `/purchase-orders/${a.purchaseOrderId}` : null),
      header: 'Request',
      sortable: true,
      render: (approval) => (
        <div className="min-w-0">
          <div className="truncate font-semibold">{approval.entityType}</div>
          <div className="mt-0.5 truncate text-xs text-muted-foreground">
            {approval.purchaseOrder?.poNumber || approval.entityId}
          </div>
        </div>
      ),
    },
    {
      key: 'details',
      header: 'Details',
      render: (approval) => (
        <div className="text-sm">
          {approval.purchaseOrder && (
            <>
              <div className="font-medium">
                {formatCurrency(
                  approval.purchaseOrder.totalAmount,
                  approval.purchaseOrder.currency
                )}
              </div>
              <div className="text-muted-foreground">
                {approval.purchaseOrder.supplier?.name || '-'}
              </div>
            </>
          )}
          {!approval.purchaseOrder && <span className="text-muted-foreground">-</span>}
        </div>
      ),
    },
    {
      key: 'requestedAt',
      header: 'Requested',
      render: (approval) => (
        <div className="text-sm">
          <div className="text-muted-foreground">{formatDate(approval.requestedAt)}</div>
        </div>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (approval) => (
        <Badge className={statusColors[approval.status]}>{approval.status}</Badge>
      ),
    },
    {
      key: 'respondedAt',
      header: 'Responded',
      render: (approval) => (
        <div className="text-sm">
          {approval.respondedAt ? (
            <>
              <div className="text-muted-foreground">{formatDate(approval.respondedAt)}</div>
              {approval.approver && (
                <div className="text-muted-foreground">by {approval.approver.fullName}</div>
              )}
            </>
          ) : (
            <span className="text-muted-foreground">-</span>
          )}
        </div>
      ),
    },
    {
      key: 'actions',
      header: '',
      className: 'w-12',
      render: (approval) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" aria-label="More actions" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {/* Rendered with no onClick — it looked like a way in and was
                inert. Purchase-order approvals are the kind that carry a
                record, so those open it; the rest no longer pretend. */}
            {approval.purchaseOrderId && (
              <DropdownMenuItem
                onClick={() => navigate(`/purchase-orders/${approval.purchaseOrderId}`)}
              >
                <Eye className="mr-2 h-4 w-4" />
                View purchase order
              </DropdownMenuItem>
            )}
            {approval.status === 'PENDING' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => setDecisionDialog({ open: true, approval, decision: 'APPROVED' })}
                >
                  <Check className="mr-2 h-4 w-4 text-success-600" />
                  Approve
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setDecisionDialog({ open: true, approval, decision: 'REJECTED' })}
                >
                  <X className="mr-2 h-4 w-4 text-danger-600" />
                  Reject
                </DropdownMenuItem>
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          title="Approvals"
          description="Requests awaiting a decision, with the account that raised each and the date it was answered."
        />
      </motion.div>

      {/* The other two queues, counted and linked rather than duplicated. */}
      {elsewhere && elsewhere.freight + elsewhere.consumption > 0 && (
        <div
          className={cn(
            PANEL,
            'mt-4 flex flex-wrap items-center gap-x-2 gap-y-1 px-4 py-3 text-sm'
          )}
        >
          <span className="text-muted-foreground">Also waiting on a decision:</span>
          {elsewhere.freight > 0 && (
            <Link
              to="/tms/rfq-approvals"
              className="font-medium underline-offset-4 hover:underline"
            >
              {elsewhere.freight} freight {elsewhere.freight === 1 ? 'award' : 'awards'}
            </Link>
          )}
          {elsewhere.freight > 0 && elsewhere.consumption > 0 && (
            <span className="text-muted-foreground">·</span>
          )}
          {elsewhere.consumption > 0 && (
            <Link
              to="/consumption?tab=approvals"
              className="font-medium underline-offset-4 hover:underline"
            >
              {elsewhere.consumption} consumption{' '}
              {elsewhere.consumption === 1 ? 'variance' : 'variances'}
            </Link>
          )}
        </div>
      )}

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIGrid columns={3}>
          <MetricCard
            label="Pending"
            value={pendingCount}
            sentiment={pendingCount > 0 ? 'warning' : 'success'}
            icon={<Clock className="h-5 w-5" />}
          />
          <MetricCard
            label="Approved"
            value={approvedCount}
            sentiment="success"
            icon={<Check className="h-5 w-5" />}
          />
          <MetricCard
            label="Rejected"
            value={rejectedCount}
            sentiment={rejectedCount > 0 ? 'danger' : 'neutral'}
            icon={<X className="h-5 w-5" />}
          />
        </KPIGrid>
      </div>

      {/* Filters */}
      <motion.div variants={fadeUp} className="flex items-center gap-4 mt-6">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="PENDING">Pending</SelectItem>
            <SelectItem value="APPROVED">Approved</SelectItem>
            <SelectItem value="REJECTED">Rejected</SelectItem>
          </SelectContent>
        </Select>
      </motion.div>

      <motion.div variants={fadeUp} className="mt-6">
        <DataTable
          data={approvals}
          columns={columns}
          loading={loading}
          pagination
          exportable
          columnToggle
          searchable
          emptyTitle="No approval requests"
          emptyDescription="All approval requests have been processed."
        />
      </motion.div>

      {/* Decision Dialog */}
      <Dialog
        open={decisionDialog.open}
        onOpenChange={(open) => {
          setDecisionDialog({ open });
          if (!open) setComments('');
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {decisionDialog.decision === 'APPROVED' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
            <DialogDescription>
              {decisionDialog.decision === 'APPROVED'
                ? 'Are you sure you want to approve this request?'
                : 'Please provide a reason for rejection.'}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder={
                decisionDialog.decision === 'APPROVED'
                  ? 'Add optional comments...'
                  : 'Reason for rejection...'
              }
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionDialog({ open: false })}>
              Cancel
            </Button>
            <Button
              variant={decisionDialog.decision === 'APPROVED' ? 'default' : 'destructive'}
              onClick={handleDecision}
              disabled={processing || (decisionDialog.decision === 'REJECTED' && !comments.trim())}
            >
              {processing
                ? 'Processing...'
                : decisionDialog.decision === 'APPROVED'
                  ? 'Approve'
                  : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.main>
  );
}
